const CART_DISCOUNT_SYNC_EVENT = 'cart-discount:sync';

/**
 * A custom element that applies discount codes to the cart drawer.
 */
class CartDiscount extends HTMLElement {
  constructor() {
    super();
    this.refs = {};
    this.boundEventHandlers = new Map();
    this.messages = this.getTranslations();
  }

  connectedCallback() {
    this.messages = this.getTranslations();
    this.initializeRefs();
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  getTranslations() {
    if (window.discountTranslations) {
      return window.discountTranslations;
    }

    return {
      invalidCode: 'The code is unfortunately invalid.',
      shippingError: 'The discount code cannot be applied to shipping.',
    };
  }

  initializeRefs() {
    this.refs.formWrapper = this.querySelector('.discount-form-wrapper');
    this.refs.form = this.querySelector('.discount-form');
    this.refs.input = this.querySelector('.discount-input');
    this.refs.clearButton = this.querySelector('.discount-clear-button');
    this.refs.applyButton = this.querySelector('.discount-apply-button');
    this.refs.errorMessage = this.querySelector('.discount-error-message');
    this.refs.errorText = this.querySelector('[ref="discountErrorText"]');
    this.refs.appliedCodesContainer = this.querySelector('.discount-applied-codes');

    this.updateClearButtonVisibility(this.refs.input, this.refs.clearButton);
  }

  bindEvents() {
    if (this.refs.form) {
      const submitHandler = (event) => {
        event.preventDefault();
        this.applyDiscount(event);
      };
      this.refs.form.addEventListener('submit', submitHandler);
      this.boundEventHandlers.set('formSubmit', {
        element: this.refs.form,
        event: 'submit',
        handler: submitHandler,
      });
    }

    if (this.refs.input) {
      const inputHandler = this.handleInputChange.bind(this);
      this.refs.input.addEventListener('input', inputHandler);
      this.boundEventHandlers.set('input', {
        element: this.refs.input,
        event: 'input',
        handler: inputHandler,
      });
    }

    if (this.refs.clearButton) {
      const clearHandler = this.clearInput.bind(this);
      this.refs.clearButton.addEventListener('click', clearHandler);
      this.boundEventHandlers.set('clearButton', {
        element: this.refs.clearButton,
        event: 'click',
        handler: clearHandler,
      });
    }

    const componentClickHandler = (event) => {
      const removeButton = event.target.closest('[data-discount-remove]');
      if (!removeButton) {
        return;
      }

      event.preventDefault();

      if (removeButton.dataset.discountRemove) {
        this.removeDiscount(removeButton.dataset.discountRemove);
      }
    };

    this.addEventListener('click', componentClickHandler);
    this.boundEventHandlers.set('componentClick', {
      element: this,
      event: 'click',
      handler: componentClickHandler,
    });
  }

  unbindEvents() {
    for (const { element, event, handler } of this.boundEventHandlers.values()) {
      element.removeEventListener(event, handler);
    }

    this.boundEventHandlers.clear();
  }

  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const controller = new AbortController();
    this.#activeFetch = controller;
    return controller;
  }

  #existingDiscounts() {
    return Array.from(this.refs.appliedCodesContainer?.querySelectorAll('.applied-code') || [])
      .map((element) => element.dataset.code?.trim())
      .filter(Boolean);
  }

  #extractErrorMessage(data, fallback = this.messages.invalidCode) {
    if (typeof data?.description === 'string' && data.description.trim()) {
      return data.description.trim();
    }

    if (typeof data?.message === 'string' && data.message.trim()) {
      return data.message.trim();
    }

    if (typeof data?.errors === 'string' && data.errors.trim()) {
      return data.errors.trim();
    }

    if (Array.isArray(data?.errors) && data.errors[0]) {
      return String(data.errors[0]);
    }

    // const discountCodes = Array.isArray(data?.discount_codes) ? data.discount_codes : [];
    // const hasApplicableDiscount = discountCodes.some((discount) => discount.applicable === true);
    // const hasRejectedDiscount = discountCodes.some((discount) => discount.applicable === false);

    // if (hasApplicableDiscount && hasRejectedDiscount) {
    //   return discountCodes
    //     .filter((discount) => discount.applicable === false)
    //     .map((discount) => `<strong>${discount.code}</strong> konnte nicht mit deinen vorhandenen Rabatten verwendet werden.`)
    //     .join(', ');
    // }

    return fallback;
  }

  #extractAppliedCodesFromHtml(sectionHtml) {
    if (typeof sectionHtml !== 'string' || !sectionHtml.trim()) {
      return [];
    }

    const documentFragment = new DOMParser().parseFromString(sectionHtml, 'text/html');

    return Array.from(documentFragment.querySelectorAll('.applied-code'))
      .map((element) => element.dataset.code?.trim())
      .filter(Boolean);
  }

  #publishCartUpdate(data) {
    if (typeof publish === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') {
      return;
    }

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'discount-component',
      cartData: data,
    });
  }

  #parseFreeGiftRules() {
    if (typeof this.dataset.freeGiftRules !== 'string' || !this.dataset.freeGiftRules.trim()) {
      return [];
    }

    try {
      return JSON.parse(this.dataset.freeGiftRules)
        .map((rule) => ({
          discountCode: typeof rule?.discountCode === 'string' ? rule.discountCode.trim() : '',
          variantId: Number(rule?.variantId),
        }))
        .filter((rule) => rule.discountCode && Number.isInteger(rule.variantId) && rule.variantId > 0);
    } catch (error) {
      console.error('Unable to parse free gift rules:', error);
      return [];
    }
  }

  #dispatchDiscountSync(appliedCodes, cart) {
    document.dispatchEvent(new CustomEvent(CART_DISCOUNT_SYNC_EVENT, {
      detail: {
        appliedCodes,
        cart,
        freeGiftRules: this.#parseFreeGiftRules(),
      },
    }));
  }

  async #updateDiscounts(discounts) {
    if (typeof this.dataset.sectionId !== 'string') {
      throw new Error('Section ID not found.');
    }

    const abortController = this.#createAbortController();
    const response = await fetch(routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        discount: discounts.join(','),
        sections: [this.dataset.sectionId],
      }),
      signal: abortController.signal,
    });

    let data = null;

    try {
      data = await response.json();
    } catch (error) {
      if (!response.ok) {
        throw new Error(response.statusText || this.messages.invalidCode);
      }

      throw error;
    }

    if (!response.ok || data?.status) {
      throw new Error(this.#extractErrorMessage(data));
    }

    return data;
  }

  showError(message) {
    if (this.refs.input) {
      this.refs.input.classList.add('discount-input--error');
    }

    const fieldWrapper = this.refs.input?.closest('.field-wrapper');
    fieldWrapper?.classList.add('field-wrapper--error');

    if (this.refs.errorText) {
      this.refs.errorText.textContent = message;
    }

    if (this.refs.errorMessage) {
      this.refs.errorMessage.hidden = false;
    }
  }

  hideError() {
    if (this.refs.input) {
      this.refs.input.classList.remove('discount-input--error');
    }

    const fieldWrapper = this.refs.input?.closest('.field-wrapper');
    fieldWrapper?.classList.remove('field-wrapper--error');

    if (this.refs.errorMessage) {
      this.refs.errorMessage.hidden = true;
    }
  }

  handleInputChange(event) {
    const input = event.target;
    const clearButton = input.parentNode.querySelector('.discount-clear-button');

    this.hideError();
    this.updateClearButtonVisibility(input, clearButton);
  }

  updateClearButtonVisibility(input, clearButton) {
    if (!input || !clearButton) {
      return;
    }

    clearButton.classList.toggle('hidden', !input.value.trim());
  }

  clearInput(event) {
    event.preventDefault();

    if (!this.refs.input) {
      return;
    }

    this.refs.input.value = '';
    this.hideError();
    this.updateClearButtonVisibility(this.refs.input, this.refs.clearButton);
    this.refs.input.focus();
  }

  resetInputValue() {
    if (!this.refs.input) {
      return;
    }

    this.refs.input.value = '';
    this.updateClearButtonVisibility(this.refs.input, this.refs.clearButton);
  }

  /**
   * Handles applying a discount to the cart.
   * @param {SubmitEvent} event
   */
  applyDiscount = async (event) => {
    event.preventDefault();

    if (!this.refs.input) {
      console.error('Discount input not found');
      return;
    }

    const discountCodeInput = this.refs.input.value.trim();

    if (!discountCodeInput) {
      this.hideError();
      return;
    }

    const existingDiscounts = this.#existingDiscounts();
    const existingDiscountsLookup = new Set(existingDiscounts.map((code) => code.toLowerCase()));
    const seenInputCodes = new Set();
    const inputCodes = discountCodeInput
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);

    const newCodesToAdd = inputCodes.filter((code) => {
      const normalizedCode = code.toLowerCase();

      if (existingDiscountsLookup.has(normalizedCode) || seenInputCodes.has(normalizedCode)) {
        return false;
      }

      seenInputCodes.add(normalizedCode);
      return true;
    });

    if (!newCodesToAdd.length) {
      this.resetInputValue();
      this.showError(this.messages.invalidCode);
      return;
    }

    try {
      const requestedDiscounts = [...existingDiscounts, ...newCodesToAdd];
      const data = await this.#updateDiscounts(requestedDiscounts);
      const appliedCodesAfterUpdate = this.#extractAppliedCodesFromHtml(data.sections?.[this.dataset.sectionId]);
      const appliedCodesLookup = new Set(appliedCodesAfterUpdate.map((code) => code.toLowerCase()));
      const missingCodes = newCodesToAdd.filter((code) => !appliedCodesLookup.has(code.toLowerCase()));

      if (missingCodes.length) {
        const fetchedMessage = this.#extractErrorMessage(data, '');
        const hasApplicableButMissingCode = missingCodes.some((code) =>
          data.discount_codes?.some((discount) => discount.code === code && discount.applicable === true)
        );

        this.resetInputValue();
        this.showError(fetchedMessage || (hasApplicableButMissingCode ? this.messages.shippingError : this.messages.invalidCode));
        return;
      }

      this.resetInputValue();
      this.hideError();
      this.#dispatchDiscountSync(appliedCodesAfterUpdate, data);
      this.#publishCartUpdate(data);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      console.error('Error applying discount:', error);
      this.resetInputValue();
      this.showError(error.message || this.messages.invalidCode);
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Removes a discount from the cart.
   * @param {string} discountCode
   */
  removeDiscount = async (discountCode) => {
    const existingDiscounts = this.#existingDiscounts();
    const updatedDiscounts = existingDiscounts.filter((code) => code !== discountCode);

    if (existingDiscounts.length === updatedDiscounts.length) {
      return;
    }

    try {
      const data = await this.#updateDiscounts(updatedDiscounts);
      const appliedCodesAfterUpdate = this.#extractAppliedCodesFromHtml(data.sections?.[this.dataset.sectionId]);
      this.hideError();
      this.#dispatchDiscountSync(appliedCodesAfterUpdate, data);
      this.#publishCartUpdate(data);
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }

      console.error('Error removing discount:', error);
      this.showError(error.message || this.messages.invalidCode);
    } finally {
      this.#activeFetch = null;
    }
  };
}

class DiscountFreeGiftManager {
  constructor() {
    this.syncQueue = Promise.resolve();
    this.handleDiscountSync = this.handleDiscountSync.bind(this);
    document.addEventListener(CART_DISCOUNT_SYNC_EVENT, this.handleDiscountSync);
  }

  handleDiscountSync(event) {
    this.syncQueue = this.syncQueue
      .then(() => this.syncFreeGift(event.detail || {}))
      .catch((error) => {
        console.error('Error syncing discount free gift:', error);
      });
  }

  async syncFreeGift(detail) {
    const freeGiftRules = this.normalizeFreeGiftRules(detail.freeGiftRules);

    if (!freeGiftRules.length) {
      return;
    }

    const appliedCodes = Array.isArray(detail.appliedCodes) ? detail.appliedCodes : [];
    const appliedCodesLookup = new Set(
      appliedCodes
        .map((code) => this.normalizeDiscountCode(code))
        .filter(Boolean)
    );
    let cart = Array.isArray(detail.cart?.items) ? detail.cart : await this.fetchCart();
    let cartChanged = false;

    for (const rule of freeGiftRules) {
      const matchingGiftLineItems = this.findGiftLineItems(cart.items, rule);
      const shouldHaveGift = appliedCodesLookup.has(rule.discountCode);

      if (!shouldHaveGift && matchingGiftLineItems.length) {
        for (const giftLineItem of matchingGiftLineItems) {
          await this.removeFreeGiftProduct(giftLineItem.key, false);
        }

        cart = await this.fetchCart();
        cartChanged = true;
        continue;
      }

      if (shouldHaveGift && !matchingGiftLineItems.length) {
        await this.addFreeGiftProduct(rule.variantId, rule.discountCode, false);
        cart = await this.fetchCart();
        cartChanged = true;
        continue;
      }

      if (matchingGiftLineItems.length > 1) {
        for (const extraGiftLineItem of matchingGiftLineItems.slice(1)) {
          await this.removeFreeGiftProduct(extraGiftLineItem.key, false);
        }

        cart = await this.fetchCart();
        cartChanged = true;
      }
    }

    if (cartChanged) {
      this.publishCart(cart);
    }
  }

  normalizeDiscountCode(code) {
    return typeof code === 'string' ? code.trim().toLowerCase() : '';
  }

  normalizeFreeGiftRules(rules) {
    if (!Array.isArray(rules)) {
      return [];
    }

    const seenRules = new Set();

    return rules
      .map((rule) => ({
        discountCode: this.normalizeDiscountCode(rule?.discountCode),
        variantId: Number(rule?.variantId),
      }))
      .filter((rule) => {
        if (!rule.discountCode || !Number.isInteger(rule.variantId) || rule.variantId <= 0) {
          return false;
        }

        const ruleKey = `${rule.discountCode}:${rule.variantId}`;

        if (seenRules.has(ruleKey)) {
          return false;
        }

        seenRules.add(ruleKey);
        return true;
      });
  }

  isDiscountGiftLineItem(item) {
    return item?.properties?._discount_gift === true || item?.properties?._discount_gift === 'true';
  }

  findGiftLineItems(items, rule) {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.filter((item) => {
      if (Number(item?.id) !== rule.variantId || !this.isDiscountGiftLineItem(item)) {
        return false;
      }

      const sourceDiscountCode = this.normalizeDiscountCode(item?.properties?._discount_code_free_gift_source);

      if (!sourceDiscountCode) {
        return true;
      }

      return sourceDiscountCode === rule.discountCode;
    });
  }

  async addFreeGiftProduct(variantId, discountCode, shouldPublish = true) {
    const response = await fetch(routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              _discount_gift: true,
              _discount_code_free_gift_source: discountCode,
            },
          },
        ],
      }),
    });

    if (!response.ok) {
      throw new Error('Unable to add discount free gift product.');
    }

    if (shouldPublish) {
      await this.publishUpdatedCart();
    }
  }

  async removeFreeGiftProduct(lineItemKey, shouldPublish = true) {
    const response = await fetch(routes.cart_update_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        updates: {
          [lineItemKey]: 0,
        },
      }),
    });

    if (!response.ok) {
      throw new Error('Unable to remove discount free gift product.');
    }

    if (shouldPublish) {
      await this.publishUpdatedCart();
    }
  }

  async fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to fetch cart.');
    }

    return response.json();
  }

  publishCart(cart) {
    if (typeof publish === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') {
      return;
    }

    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'discount-free-gift',
      cartData: cart,
    });
  }

  async publishUpdatedCart() {
    const cart = await this.fetchCart();
    this.publishCart(cart);
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}

if (!window.discountFreeGiftManager) {
  window.discountFreeGiftManager = new DiscountFreeGiftManager();
}
